import React, { useState } from 'react';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Title from 'shared/components/title/Title';
import { Formik, Form, useField } from 'formik';
import Loader from 'features/Loader/Loader';
import SelectDropdown from 'shared/components/select-dropdown/SelectDropdown';
import { addIngredientSchema } from 'utils/validators/nutrition';
import { isNotPositiveNum } from 'utils/strings';
import { roundNum } from 'utils/numbers';
import NumberFormat from 'react-number-format';

const AddIngredientData = ({
  handleReturnToInitialStep,
  handleIngredientSubmission,
  headerTitle,
  nutritionPlan,
  initialIngredient,
  measurementsUnits,
  loadingMeasurements
}) => {
  const {
    quantity_size,
    weight_grams,
    protein,
    carbs,
    fat,
    calories,
    measurement_unit,
    original_weight,
    original_calories,
    original_measure,
    original_quantity
  } = initialIngredient;

  const parsedInitialIngredient = {
    ...initialIngredient,
    quantity_size: quantity_size ? Number(quantity_size) : 1,
    weight_grams: weight_grams ? Number(weight_grams) : 0,
    protein: protein ? Number(protein) : 0,
    carbs: carbs ? Number(carbs) : 0,
    fat: fat ? Number(fat) : 0,
    calories: calories ? Number(calories) : 0,
    measurement_unit: measurement_unit,
    original_weight: original_weight ? Number(original_weight) : (
      weight_grams ? Number(weight_grams) : 0
    ),
    original_calories: original_calories ? Number(original_calories) : (
      calories ? Number(calories) : 0
    ),
    original_measure: original_measure ? original_measure : measurement_unit,
    original_quantity: original_quantity ? Number(original_quantity) : (quantity_size ? Number(quantity_size) : 1)
  };

  const [newIngredient, setNewIngredient] = useState(parsedInitialIngredient);

  const handleServingSizeChange = e => {
    const {
      quantity_size,
      weight_grams,
      protein,
      carbs,
      fat,
      calories
    } = newIngredient;
    const val = e.target.value;
    const newSize = isNotPositiveNum(val) ? 1 : Number(val);
    setNewIngredient({
      ...newIngredient,
      weight_grams: (newSize * (weight_grams / quantity_size)),
      protein: (newSize * (protein / quantity_size)),
      carbs: (newSize * (carbs / quantity_size)),
      fat: (newSize * (fat / quantity_size)),
      calories: (newSize * (calories / quantity_size)),
      quantity_size: newSize
    });
  };

  const handleMeasureUnitChange = value => {
    const {
      quantity_size,
      weight_grams,
      protein,
      carbs,
      fat,
      calories
    } = newIngredient;
    const prevWeight = Number(weight_grams);

    const unitWeight = Number(unitsWeightAndServingSize[`${value}`].weight) || 0;
    const newQuantitySize = Number(unitsWeightAndServingSize[`${value}`].qty) || 1;
    const newWeight = unitWeight ;
    setNewIngredient({
      ...newIngredient,
      measurement_unit: value,

      weight_grams: (newWeight),
      protein: ((newWeight * protein)/ prevWeight),
      carbs: ((newWeight * carbs)/ prevWeight),
      fat: ((newWeight * fat)/ prevWeight),
      calories: ((newWeight * calories)/ prevWeight),
      quantity_size: newQuantitySize
    });
  };

  let units = measurementsUnits && measurementsUnits.length ? (measurementsUnits.map(unit =>{
    return {
      option: unit.measure,
      value: unit.measure
    };
  })) : []

  let unitsWeightAndServingSize = {};
  measurementsUnits && measurementsUnits.length && measurementsUnits.forEach(unit =>{
    unitsWeightAndServingSize[`${unit.measure}`] = {
      weight: unit.serving_weight,
      qty: unit.qty
    }
  })

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        servingSize: newIngredient.quantity_size,
        measurementUnit: newIngredient.measurement_unit
      }}
      onSubmit={() => {
        handleIngredientSubmission(newIngredient);
      }}
      validationSchema={addIngredientSchema}
    >
      {formik => (
        <Form>
          {formik.isSubmitting || loadingMeasurements ? (
            <Loader />
          ) : (
            <div className="add-ingredient">
              <div className="container">
                <Breadcrumb>
                  <Breadcrumb.Item href="/nutrition">Nutrition</Breadcrumb.Item>
                  <Breadcrumb.Item href={`/nutrition/${nutritionPlan.id}`}>
                    {nutritionPlan.name}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item onClick={handleReturnToInitialStep}>
                    {headerTitle}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>Add Ingredient</Breadcrumb.Item>
                </Breadcrumb>
                <Title
                  pageTitle="Add Ingredient"
                  buttonText="Save"
                  linkText="Cancel"
                  cancelBtn={handleReturnToInitialStep}
                  disabled={loadingMeasurements}
                />
                <div className="ingredient-data-page">
                  <h2>{newIngredient.name}</h2>
                  <div className="nutrition-data">
                    <div className="data-block">
                      <ServingItem
                        fieldName="servingSize"
                        label="Serving Size"
                        placeholder="Input Quantity"
                        onChange={handleServingSizeChange}
                      />
                    </div>

                    {
                      units && units.length ? (
                        <div className="data-block">
                          <SelectDropdown
                            name="measurementUnit"
                            options={units}
                            label="Serving Unit"
                            className="form-control"
                            handleChange={
                              (fieldName, value) => {
                                value && handleMeasureUnitChange(value);
                              }
                            }
                          />
                        </div>
                      ) : (
                        <DataItem
                          label="Serving Unit"
                          value={parsedInitialIngredient.measurement_unit}
                        />
                      )
                    }

                    <DataItem
                      label="Grams (g)"
                      value={roundNum(newIngredient.weight_grams)}
                    />
                    <DataItem
                      label="Protein (g)"
                      value={roundNum(newIngredient.protein)}
                    />
                    <DataItem label="Fat (g)" value={roundNum(newIngredient.fat)} />
                    <DataItem label="Carbs (g)" value={roundNum(newIngredient.carbs)} />
                    <DataItem
                      label="Calories (g)"
                      value={roundNum(newIngredient.calories)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

const ServingItem = ({ fieldName, label, placeholder, onChange }) => {
  const [field, meta] = useField(fieldName);

  return (
    <div className="form-group">
      <label className="form-label" htmlFor={fieldName}>
        {label}
      </label>
      <NumberFormat
        type="text"
        allowNegative={false}
        className="form-control"
        placeholder={placeholder}
        name={field.name}
        value={field.value}
        onChange={e => {
          field.onChange(e);
          onChange(e);
        }}
        isAllowed={values =>
          values.value === '' || values.floatValue > 0
        }
      />
      {meta.touched && meta.error ? (
        <div className="input-feedback">{meta.error}</div>
      ) : null}
    </div>
  );
};

const DataItem = ({ label, value }) => {
  return (
    <div className="data-block">
      <label>{label}</label>
      <span>{`${value}g`}</span>
    </div>
  );
};

export default AddIngredientData;
